import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { requestAccountDeletion } from "@/api/authApi";
import { useAuth } from "@/auth/AuthContext";

export default function DeleteAccountPage() {
  const { isAuthenticated, email } = useAuth();
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [company, setCompany] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = "Delete your account — ChikitsaLink";
    return () => {
      document.title = previous;
    };
  }, []);

  const submit = async () => {
    setError(null);
    if (!confirmed) {
      setError("Confirm that you want your ChikitsaLink account and personal data deleted.");
      return;
    }
    setSubmitting(true);
    try {
      await requestAccountDeletion({
        email: isAuthenticated ? email : address.trim(),
        note: note.trim() || undefined,
        source: "WEB",
        company,
      });
      setDone(true);
    } catch {
      setError("We could not submit the request. Check the email address and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography variant="overline" color="primary.dark" fontWeight={700}>
        ChikitsaLink
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
        Delete your account and personal data
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Use this page for the ChikitsaLink website and the ChikitsaLink Android
        app. You do not have to be signed in. In the app, the same request is
        under Settings, then Delete my account and data.
      </Typography>

      <Stack spacing={2} sx={{ mb: 4 }}>
        <Step n={1} text="Enter the email address on your ChikitsaLink account." />
        <Step n={2} text="Confirm that you want the account and personal data deleted." />
        <Step n={3} text="Submit the request. We email that address to confirm we received it." />
      </Stack>

      <Box component="section" sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 1.5 }}>
          What we delete and what we keep
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
          Within 30 days we delete the account, profile, appointment notes,
          files you uploaded, doctor documents you uploaded, and device
          notification tokens.
        </Typography>
        <Typography color="text.secondary">
          Prescriptions, payments, and refund records are kept for as long as
          Indian tax and medical-record rules require, then deleted or stored
          without your name. Signing out or uninstalling the app does not
          delete this data.
        </Typography>
      </Box>

      {done ? (
        <Alert severity="success">
          Request received. If this email has a ChikitsaLink account, we will
          delete the personal data described above within 30 days and send a
          confirmation to that address.
        </Alert>
      ) : (
        <Stack spacing={2} component="form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          {isAuthenticated && email ? (
            <TextField label="Account email" value={email} disabled />
          ) : (
            <TextField
              label="Account email"
              type="email"
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="email"
            />
          )}
          <TextField
            label="Anything we should know (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
          />
          <TextField
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            sx={{ position: "absolute", left: "-9999px" }}
          />
          <FormControlLabel
            control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
            label="I want ChikitsaLink to delete this account and the personal data described above."
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" color="error" disabled={submitting} sx={{ alignSelf: "flex-start" }}>
            {submitting ? "Submitting…" : "Submit deletion request"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Related: <RouterLink to="/privacy">Privacy policy</RouterLink>
          </Typography>
        </Stack>
      )}
    </Container>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <Typography color="text.secondary">
      {n}. {text}
    </Typography>
  );
}
